/**
 * 会话列表页（T06）。
 *
 * 职责：
 * - 列出本地会话（`conversationRepo.listConversationsPage`），显示标题 / 最后消息时间 / 消息数；
 * - 新建会话（写入 IndexedDB 并切到对话页）、进入对话（记住活动会话后跳 `#/chat`）；
 * - 长按（或右键）会话卡片弹出二次确认后删除（会话与消息级联删除）；
 * - 每个可见部件都通过 `useComponentMeta()` 登记 390x844 逻辑坐标（安卓移植清单数据源）；
 * - 动效一律引用 `src/design/motion.ts` 的冻结预设（tap / listStagger / sheetBackdrop）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { liveQuery } from 'dexie';

import { navigate } from '../../app/hashRoute';
import { conversationRepo, messageRepo } from '../../core/repositories';
import { loadModelSettings } from '../../core/settings';
import { loadModelPreference } from '../chat/modelPreference';
import type { Conversation } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import type { MotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { ComponentMetaInput } from '../../ui';
import { setActiveConversationId, getActiveConversationId } from '../chat/activeConversation';
import styles from './ConversationsPage.module.css';
import {
  PAGE_TOP,
  ROW_HEIGHT,
  conversationsComponentMeta,
  conversationsLayout,
  conversationItemLayout,
} from './componentMeta';

const PAGE_TITLE = '会话列表';
const LONG_PRESS_MS = 550;

/** 页面内的绝对定位：清单坐标是画布绝对坐标，DOM 坐标相对 `origin`（默认页面根容器）。 */
function boxStyle(
  layout: ComponentMetaInput['layout'],
  zIndex: number,
  origin: { x: number; y: number } = { x: 0, y: PAGE_TOP },
): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x - origin.x,
    top: layout.y - origin.y,
    width: layout.width,
    height: layout.height,
    zIndex,
  };
}

/** 最后消息时间：当天显示 HH:mm，跨天显示 M/D HH:mm。 */
function formatUpdatedAt(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return `${hh}:${mm}`;
  return `${date.getMonth() + 1}/${date.getDate()} ${hh}:${mm}`;
}

interface ConversationRowProps {
  conversation: Conversation;
  index: number;
  presets: MotionPresets;
  onOpen: (conversation: Conversation) => void;
  onRequestDelete: (conversation: Conversation) => void;
  generating: boolean;
}

/** 单张会话卡片：点击进入对话，长按 / 右键请求删除（删除需二次确认）。 */
function ConversationRow({
  conversation,
  index,
  presets,
  onOpen,
  onRequestDelete,
  generating,
}: ConversationRowProps): ReactElement {
  const layout = conversationItemLayout(index);
  const baseId = `conversations.item.${conversation.id}`;

  const rowMeta: ComponentMetaInput = {
    id: baseId,
    page: 'conversations',
    role: 'button',
    label: `会话：${conversation.title}`,
    layout,
    zIndex: 4,
    props: ['title', 'updatedAt', 'messageCount'],
    notes: '会话卡片实例（模板坐标见 conversations.item）；安卓用 RecyclerView item',
  };
  const titleMeta: ComponentMetaInput = {
    id: `${baseId}.title`,
    page: 'conversations',
    role: 'text',
    label: `会话标题：${conversation.title}`,
    layout: { x: 16, y: layout.y + 12, width: 300, height: 24 },
    zIndex: 5,
    notes: '单行省略号',
  };
  const infoMeta: ComponentMetaInput = {
    id: `${baseId}.meta`,
    page: 'conversations',
    role: 'text',
    label: '最后消息时间与消息数',
    layout: { x: 16, y: layout.y + 40, width: 300, height: 18 },
    zIndex: 5,
    notes: '格式：最后消息时间 · N 条消息',
  };

  const rowRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(rowMeta, 'button'));
  const titleRef = useComponentMeta<HTMLSpanElement>(buildComponentMeta(titleMeta, 'text'));
  const infoRef = useComponentMeta<HTMLSpanElement>(buildComponentMeta(infoMeta, 'text'));

  const timerRef = useRef<number | null>(null);
  const longPressedRef = useRef(false);

  const clearPressTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearPressTimer, [clearPressTimer]);

  const handlePointerDown = (): void => {
    longPressedRef.current = false;
    clearPressTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      longPressedRef.current = true;
      onRequestDelete(conversation);
    }, LONG_PRESS_MS);
  };

  const handleClick = (): void => {
    if (longPressedRef.current) {
      longPressedRef.current = false;
      return;
    }
    onOpen(conversation);
  };

  return (
    <motion.li
      className={styles.rowWrap}
      variants={presets.listStagger.item}
      style={{
        position: 'absolute',
        left: 0,
        top: index * ROW_HEIGHT,
        width: '100%',
        height: ROW_HEIGHT,
      }}
    >
      <motion.button
        ref={rowRef}
        type="button"
        className={styles.row}
        data-ui-id={baseId}
        data-ui-role="button"
        data-skin-surface="panel"
        aria-label={`会话：${conversation.title}`}
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onPointerDown={handlePointerDown}
        onPointerUp={clearPressTimer}
        onPointerCancel={clearPressTimer}
        onPointerLeave={clearPressTimer}
        onClick={handleClick}
        onContextMenu={(event) => {
          event.preventDefault();
          onRequestDelete(conversation);
        }}
      >
        <span ref={titleRef} className={styles.rowTitle} data-ui-id={`${baseId}.title`}>
          {conversation.title}
        </span>
        {generating ? <span className={styles.generatingIndicator} data-ui-id={`${baseId}.generating`} role="status" aria-label="正在生成" title="正在生成" /> : null}
        <span ref={infoRef} className={styles.rowMeta} data-ui-id={`${baseId}.meta`}>
          {`${formatUpdatedAt(conversation.updatedAt)} · ${conversation.messageCount} 条消息`}
        </span>
      </motion.button>
    </motion.li>
  );
}

/** 空状态：没有任何本地会话时的引导。 */
function EmptyState({
  presets,
  onCreate,
}: {
  presets: MotionPresets;
  onCreate: () => void;
}): ReactElement {
  const boxRef = useComponentMeta<HTMLDivElement>(
    conversationsComponentMeta('conversations.empty'),
  );
  const titleRef = useComponentMeta<HTMLHeadingElement>(
    conversationsComponentMeta('conversations.empty.title'),
  );
  const hintRef = useComponentMeta<HTMLParagraphElement>(
    conversationsComponentMeta('conversations.empty.hint'),
  );
  const actionRef = useComponentMeta<HTMLButtonElement>(
    conversationsComponentMeta('conversations.empty.actionButton'),
  );

  const origin = { x: 0, y: PAGE_TOP };
  return (
    <>
      <div
        ref={boxRef}
        className={styles.empty}
        style={boxStyle(conversationsLayout('conversations.empty'), 3, origin)}
        data-ui-id="conversations.empty"
        data-ui-role="container"
      />
      <h2
        ref={titleRef}
        className={styles.emptyTitle}
        style={boxStyle(conversationsLayout('conversations.empty.title'), 4, origin)}
        data-ui-id="conversations.empty.title"
      >
        还没有会话
      </h2>
      <p
        ref={hintRef}
        className={styles.emptyHint}
        style={boxStyle(conversationsLayout('conversations.empty.hint'), 4, origin)}
        data-ui-id="conversations.empty.hint"
      >
        新建一个会话，开始和你的角色对话
      </p>
      <motion.button
        ref={actionRef}
        type="button"
        className={styles.primaryButton}
        style={boxStyle(conversationsLayout('conversations.empty.actionButton'), 4, origin)}
        data-ui-id="conversations.empty.actionButton"
        aria-label="新建第一个会话"
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={onCreate}
      >
        新建会话
      </motion.button>
    </>
  );
}

interface DeleteDialogProps {
  conversation: Conversation;
  presets: MotionPresets;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** 二次确认弹层：长按 / 右键会话卡片后出现。 */
function DeleteDialog({
  conversation,
  presets,
  busy,
  onCancel,
  onConfirm,
}: DeleteDialogProps): ReactElement {
  const backdropRef = useComponentMeta<HTMLDivElement>(
    conversationsComponentMeta('conversations.deleteBackdrop'),
  );
  const dialogRef = useComponentMeta<HTMLDivElement>(
    conversationsComponentMeta('conversations.deleteDialog'),
  );
  const titleRef = useComponentMeta<HTMLHeadingElement>(
    conversationsComponentMeta('conversations.deleteDialog.title'),
  );
  const messageRef = useComponentMeta<HTMLParagraphElement>(
    conversationsComponentMeta('conversations.deleteDialog.message'),
  );
  const cancelRef = useComponentMeta<HTMLButtonElement>(
    conversationsComponentMeta('conversations.deleteDialog.cancelButton'),
  );
  const confirmRef = useComponentMeta<HTMLButtonElement>(
    conversationsComponentMeta('conversations.deleteDialog.confirmButton'),
  );

  const root = { x: 0, y: PAGE_TOP };
  const dialogLayout = conversationsLayout('conversations.deleteDialog');
  const dialogOrigin = { x: dialogLayout.x, y: dialogLayout.y };

  return (
    <>
      <motion.div
        ref={backdropRef}
        className={styles.backdrop}
        style={boxStyle(conversationsLayout('conversations.deleteBackdrop'), 30, root)}
        data-ui-id="conversations.deleteBackdrop"
        data-ui-role="modal"
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
        onClick={onCancel}
      />
      <motion.div
        ref={dialogRef}
        className={styles.dialog}
        style={boxStyle(dialogLayout, 31, root)}
        data-ui-id="conversations.deleteDialog"
        data-ui-role="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversations-delete-title"
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
      >
        <h2
          ref={titleRef}
          id="conversations-delete-title"
          className={styles.dialogTitle}
          style={boxStyle(
            conversationsLayout('conversations.deleteDialog.title'),
            32,
            dialogOrigin,
          )}
          data-ui-id="conversations.deleteDialog.title"
        >
          删除会话？
        </h2>
        <p
          ref={messageRef}
          className={styles.dialogMessage}
          style={boxStyle(
            conversationsLayout('conversations.deleteDialog.message'),
            32,
            dialogOrigin,
          )}
          data-ui-id="conversations.deleteDialog.message"
        >
          {`「${conversation.title}」及其全部消息会一起删除，且无法恢复。`}
        </p>
        <motion.button
          ref={cancelRef}
          type="button"
          className={styles.secondaryButton}
          style={boxStyle(
            conversationsLayout('conversations.deleteDialog.cancelButton'),
            32,
            dialogOrigin,
          )}
          data-ui-id="conversations.deleteDialog.cancelButton"
          aria-label="取消删除"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onCancel}
        >
          取消
        </motion.button>
        <motion.button
          ref={confirmRef}
          type="button"
          className={styles.dangerButton}
          style={boxStyle(
            conversationsLayout('conversations.deleteDialog.confirmButton'),
            32,
            dialogOrigin,
          )}
          data-ui-id="conversations.deleteDialog.confirmButton"
          aria-label="确认删除"
          disabled={busy}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onConfirm}
        >
          {busy ? '删除中…' : '删除'}
        </motion.button>
      </motion.div>
    </>
  );
}

export function ConversationsPage(): ReactElement {
  const presets = useMotionPresets();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Conversation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingConversationIds, setGeneratingConversationIds] = useState<Set<string>>(() => new Set());

  const rootRef = useComponentMeta<HTMLElement>(
    conversationsComponentMeta('page.conversations.root'),
  );
  const headerRef = useComponentMeta<HTMLDivElement>(
    conversationsComponentMeta('conversations.header'),
  );
  const headerTitleRef = useComponentMeta<HTMLHeadingElement>(
    conversationsComponentMeta('conversations.header.title'),
  );
  const newButtonRef = useComponentMeta<HTMLButtonElement>(
    conversationsComponentMeta('conversations.newButton'),
  );
  const listRef = useComponentMeta<HTMLDivElement>(
    conversationsComponentMeta('conversations.list'),
  );
  const errorRef = useComponentMeta<HTMLParagraphElement>(
    conversationsComponentMeta('conversations.errorBanner.message'),
  );
  const errorBoxRef = useComponentMeta<HTMLDivElement>(
    conversationsComponentMeta('conversations.errorBanner'),
  );

  useEffect(() => {
    let alive = true;
    void (async (): Promise<void> => {
      try {
        const page = await conversationRepo.listConversationsPage({ limit: 100 });
        if (!alive) return;
        setConversations(page.items);
        setError(null);
      } catch {
        if (alive) setError('读取本地会话失败');
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const subscription = liveQuery(() => messageRepo.listGeneratingConversationIds()).subscribe(ids => {
      setGeneratingConversationIds(new Set(ids));
    });
    return () => subscription.unsubscribe();
  }, []);

  const createConversation = useCallback(async (): Promise<void> => {
    try {
      const preference = loadModelPreference();
      const conversation = await conversationRepo.createAndSaveConversation({
        settings: loadModelSettings(),
        ...(preference ?? {}),
      });
      setConversations((prev) => [conversation, ...prev]);
      setActiveConversationId(conversation.id);
      navigate('/chat');
    } catch {
      setError('新建会话失败');
    }
  }, []);

  const openConversation = useCallback((conversation: Conversation): void => {
    setActiveConversationId(conversation.id);
    navigate('/chat');
  }, []);

  const confirmDelete = useCallback(async (): Promise<void> => {
    const target = pendingDelete;
    if (target === null) return;
    setDeleting(true);
    try {
      await conversationRepo.deleteConversation(target.id);
      if (getActiveConversationId() === target.id) setActiveConversationId(null);
      setConversations((prev) => prev.filter((item) => item.id !== target.id));
      setPendingDelete(null);
      setError(null);
    } catch {
      setError('删除会话失败');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete]);

  return (
    <section
      ref={rootRef}
      className={styles.page}
      data-ui-id="page.conversations.root"
      data-ui-role="container"
      data-skin-surface="page"
    >
      <div
        ref={headerRef}
        className={styles.header}
        style={boxStyle(conversationsLayout('conversations.header'), 3)}
        data-ui-id="conversations.header"
        data-ui-role="container"
      >
        <h1
          ref={headerTitleRef}
          className={styles.title}
          style={boxStyle(conversationsLayout('conversations.header.title'), 4)}
          data-ui-id="conversations.header.title"
        >
          {PAGE_TITLE}
        </h1>
        <motion.button
          ref={newButtonRef}
          type="button"
          className={styles.newButton}
          style={boxStyle(conversationsLayout('conversations.newButton'), 4)}
          data-ui-id="conversations.newButton"
          aria-label="新建会话"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => {
            void createConversation();
          }}
        >
          ＋
        </motion.button>
      </div>

      <div
        ref={listRef}
        className={styles.listBox}
        style={boxStyle(conversationsLayout('conversations.list'), 3)}
        data-ui-id="conversations.list"
        data-ui-role="list"
      >
        <motion.ul
          className={styles.list}
          aria-label="会话列表"
          style={{ height: conversations.length * ROW_HEIGHT }}
          variants={presets.listStagger.container}
          initial="hidden"
          animate="visible"
        >
          {conversations.map((conversation, index) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              index={index}
              presets={presets}
              onOpen={openConversation}
              onRequestDelete={setPendingDelete}
              generating={generatingConversationIds.has(conversation.id)}
            />
          ))}
        </motion.ul>
      </div>

      {loaded && conversations.length === 0 ? (
        <EmptyState
          presets={presets}
          onCreate={() => {
            void createConversation();
          }}
        />
      ) : null}

      {error !== null ? (
        <>
          <div
            ref={errorBoxRef}
            className={styles.errorBanner}
            style={boxStyle(conversationsLayout('conversations.errorBanner'), 6)}
            data-ui-id="conversations.errorBanner"
            data-ui-role="container"
          />
          <p
            ref={errorRef}
            className={styles.errorText}
            style={boxStyle(conversationsLayout('conversations.errorBanner.message'), 7)}
            data-ui-id="conversations.errorBanner.message"
            role="alert"
          >
            {error}
          </p>
        </>
      ) : null}

      <AnimatePresence>
        {pendingDelete !== null ? (
          <DeleteDialog
            conversation={pendingDelete}
            presets={presets}
            busy={deleting}
            onCancel={() => setPendingDelete(null)}
            onConfirm={() => {
              void confirmDelete();
            }}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
}
